import { referUser } from "@/services/user.service";
import { toast } from "sonner";
import { emailSchema } from "@/schemas/user.schemas";

export function isValidEmail(email: string): boolean {
  return emailSchema.safeParse(email).success;
}

export function buildSocialShareUrl(
  platform: "twitter" | "linkedin" | "facebook",
  referralLink: string
): string {
  const text = "Check out AI Legal Assistant! Get $100 credit to trial it.";
  switch (platform) {
    case "twitter":
      return `https://twitter.com/intent/tweet?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(text)}`;
    case "linkedin":
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}`;
    case "facebook":
      return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`;
  }
}

export function getReferralLink(
  user: { referral_link?: string; email?: string } | undefined
): string {
  return (
    user?.referral_link ??
    `${process.env.APP_FRONTEND_URL ?? "https://app-dev.legalassistant.au"}/auth/register?refer_by=${encodeURIComponent(user?.email ?? "")}`
  );
}

export function tryAddEmail(
  emailInput: string,
  emails: string[],
  setEmails: (emails: string[]) => void,
  setInputValue: (value: string) => void
): void {
  const trimmedEmail = emailInput.trim();
  if (trimmedEmail.length === 0) return;

  if (!isValidEmail(trimmedEmail)) {
    toast.error(`Invalid email: ${trimmedEmail}`);
    return;
  }
  if (emails.includes(trimmedEmail)) {
    toast.error(`Email already added: ${trimmedEmail}`);
    return;
  }
  if (emails.length >= 5) {
    toast.error("You can only send 5 invitations at a time.");
    return;
  }
  setEmails([...emails, trimmedEmail]);
  setInputValue("");
}

export async function sendInvitations(
  emails: string[],
  inputValue: string,
  setIsSending: (v: boolean) => void,
  setEmails: (emails: string[]) => void,
  setInputValue: (value: string) => void,
  onOpenChange?: (open: boolean) => void
): Promise<void> {
  if (emails.length === 0) {
    if (inputValue.trim().length > 0) {
      // Do not silently add — user must confirm with Enter/comma first
      toast.error("Press Enter or comma to confirm the email address before sending.");
      return;
    }
    toast.error("Please add at least one email address.");
    return;
  }
  setIsSending(true);
  try {
    await referUser({ referEmails: emails });
    toast.success("Invitations sent successfully!");
    setEmails([]);
    setInputValue("");
    onOpenChange?.(false);
  } catch (error) {
    console.error(
      "sendInvitations failed:",
      error instanceof Error ? error.message : "unknown error"
    );
    toast.error("Failed to send invitations. Please try again.");
  } finally {
    setIsSending(false);
  }
}
